import gsap from 'gsap';
import Draggable from 'gsap/Draggable';
import InertiaPlugin from 'gsap/InertiaPlugin';
import $ from '../core/Dom';
import Viewport from '../core/Viewport';

gsap.registerPlugin(Draggable, InertiaPlugin);

/**
 * Generic horizontal drag-slider. Drives any [data-track] row of fixed-width
 * slides: drag/flick to slide with inertia, clamped to bounds. Dragging is only
 * enabled when the track actually overflows the container — when everything
 * fits, the track stays put. Content-agnostic (images, cards, teasers, …).
 */
export default (el, props) => {
    const $el = $(el);
    const track = $el.find('[data-track]').get(0);

    // No track, nothing to drag
    if (!track) {
        return {
            init: () => {},
            destroy: () => {}
        };
    }

    // Slack to absorb sub-pixel rounding from the slides' calc() widths, so a
    // row that fits exactly doesn't register as draggable
    const FIT_TOLERANCE = 2;

    let draggable = null;

    // Compensate for a padded wrapper's right padding being dropped from
    // scrollWidth. When slides overflow a wrapper that has horizontal padding
    // (e.g. .pad-wrapper), the wrapper's right padding ends up *left* of the
    // overflowing slides, so it never makes it into track.scrollWidth — and the
    // end of the track loses the trailing gutter the start has. Only kicks in
    // for that exact case: a single content child, with right padding, whose own
    // content overflows it. Anything else (no wrapper, no padding, content that
    // fits) is left untouched, so this stays safe for other layouts.
    const getTrailingPad = () => {
        const content = track.firstElementChild;

        if (!content) {
            return 0;
        }

        const padRight = parseFloat(getComputedStyle(content).paddingRight) || 0;

        if (padRight <= 0 || content.scrollWidth - content.clientWidth <= 1) {
            return 0;
        }

        return padRight;
    };

    // How far the track can travel left before its right edge meets the viewport
    const getMaxScroll = () => Math.max(0, track.scrollWidth + getTrailingPad() - el.clientWidth);

    const build = () => {
        if (draggable) {
            draggable.kill();
            draggable = null;
        }

        const maxScroll = getMaxScroll();

        // Everything fits at this breakpoint — no sliding needed
        if (maxScroll <= FIT_TOLERANCE) {
            gsap.set(track, { x: 0 });
            $el.removeClass('is-draggable');
            return;
        }

        // Keep the current position valid against the new bounds
        const x = gsap.utils.clamp(-maxScroll, 0, gsap.getProperty(track, 'x'));
        gsap.set(track, { x });

        $el.addClass('is-draggable');

        draggable = Draggable.create(track, {
            type: 'x',
            inertia: true,
            bounds: { minX: -maxScroll, maxX: 0 },
            edgeResistance: 0.97,
            dragResistance: 0.2,
            friction: 0.2,
            cursor: 'grab',
            activeCursor: 'grabbing',
            allowContextMenu: true
        })[0];
    };

    const onResize = () => {
        build();
    };

    const init = () => {
        build();
        Viewport.on('resize', onResize);
    };

    const destroy = () => {
        Viewport.off('resize', onResize);
        if (draggable) {
            draggable.kill();
            draggable = null;
        }
        gsap.killTweensOf(track);
    };

    return {
        init,
        destroy
    };
};
